import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';

/** A single, finite illustrative sequence. Never sends a report or claims a fix. */
export function useSupportStory(replay: number, reducedMotion: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!ref.current || reducedMotion) return;
    const context = gsap.context(() => {
      const timeline = gsap.timeline({ defaults: { ease: 'power2.inOut' } });
      gsap.set('.support-cursor', { x: 20, y: 22, opacity: 0 });
      gsap.set('.support-clipboard', {
        y: 18,
        opacity: 0,
        rotation: -8,
        transformOrigin: '50% 80%',
      });
      gsap.set('.support-message', { y: 8, opacity: 0 });
      gsap.set('.support-check, .support-spark, .support-fixed', {
        opacity: 0,
      });
      gsap.set('.support-code-line', {
        scaleX: 0,
        transformOrigin: 'left center',
      });
      gsap.set('.support-packet', { x: -28, opacity: 0 });
      gsap.set('.support-route', { strokeDasharray: 1, strokeDashoffset: 1 });
      gsap.set('.support-bug', { opacity: 1 });
      timeline
        .to('.support-cursor', { x: 0, y: 0, opacity: 1, duration: 0.55 }, 0.1)
        .to(
          '.support-copy-target',
          {
            scale: 0.94,
            transformOrigin: 'center',
            duration: 0.13,
            yoyo: true,
            repeat: 1,
          },
          0.65
        )
        .to(
          '.support-clipboard',
          {
            y: 0,
            rotation: 0,
            opacity: 1,
            duration: 0.65,
            ease: 'back.out(1.5)',
          },
          0.8
        )
        .to('.support-cursor', { opacity: 0, duration: 0.25 }, 1.1)
        .to('.support-left-check', { opacity: 1, duration: 0.2 }, 1.35)
        .to('.support-route', { strokeDashoffset: 0, duration: 0.6 }, 1.1)
        .to('.support-packet', { opacity: 1, x: 0, duration: 0.3 }, 1.4)
        .to('.support-packet', { x: 27, opacity: 0, duration: 0.5 }, 1.7)
        .to(
          '.support-message',
          { opacity: 1, y: 0, duration: 0.5, stagger: 0.12 },
          1.95
        )
        .to(
          '.support-code-line',
          { scaleX: 1, duration: 0.4, stagger: 0.15 },
          2.55
        )
        .to(
          '.support-wrench',
          {
            rotation: -18,
            transformOrigin: 'center',
            duration: 0.18,
            repeat: 3,
            yoyo: true,
          },
          2.85
        )
        .to('.support-bug', { opacity: 0, duration: 0.2 }, 3.45)
        .to(
          '.support-fixed, .support-right-check',
          { opacity: 1, duration: 0.35 },
          3.65
        )
        .fromTo(
          '.support-spark',
          { scale: 0.3, opacity: 0, transformOrigin: 'center' },
          { scale: 1, opacity: 1, duration: 0.3, stagger: 0.05 },
          3.75
        )
        .to('.support-spark', { opacity: 0, duration: 0.45 }, 4.15);
    }, ref);
    return () => context.revert();
  }, [replay, reducedMotion]);
  return ref;
}

export function CopyScene() {
  return (
    <svg
      viewBox="0 0 320 228"
      className="support-scene"
      aria-hidden="true"
      focusable="false"
    >
      <ellipse cx="162" cy="207" rx="126" ry="8" className="support-shadow" />
      <path
        d="M110 201h100m-69-30-5 29m34-29 5 29"
        className="support-outline"
      />
      <rect
        x="46"
        y="29"
        width="230"
        height="150"
        rx="10"
        className="support-window"
      />
      <path d="M47 54h228" className="support-outline" />
      <g className="support-muted">
        <circle cx="58" cy="42" r="2" />
        <circle cx="66" cy="42" r="2" />
        <circle cx="74" cy="42" r="2" />
      </g>
      <text x="89" y="45" className="support-tiny-text">
        STUDIO VAULT
      </text>
      <rect
        x="61"
        y="69"
        width="177"
        height="11"
        rx="3"
        className="support-line-muted"
      />
      <text x="66" y="77" className="support-micro-text">
        DIAGNOSTIC REPORT
      </text>
      <g className="support-line">
        <rect
          className="support-screen-line"
          x="62"
          y="91"
          width="109"
          height="4"
          rx="2"
        />
        <rect
          className="support-screen-line"
          x="62"
          y="102"
          width="145"
          height="4"
          rx="2"
        />
        <rect
          className="support-screen-line"
          x="62"
          y="113"
          width="76"
          height="4"
          rx="2"
        />
      </g>
      <g className="support-copy-target">
        <rect
          x="62"
          y="133"
          width="89"
          height="25"
          rx="5"
          className="support-accent-fill"
        />
        <rect
          x="73"
          y="140"
          width="8"
          height="9"
          rx="1"
          className="support-button-ink"
        />
        <path d="M76 138h8v9" className="support-button-stroke" />
        <text x="93" y="149" className="support-button-text">
          COPY
        </text>
      </g>
      <g className="support-person">
        <circle cx="34" cy="158" r="12" className="support-person-head" />
        <path
          d="M22 155q-1-18 14-12 13 0 10 15l-7-8-16 8"
          className="support-hair"
        />
        <path
          d="M18 205v-25q14-15 30 0l15 8 24-18"
          className="support-sleeve"
        />
        <path d="m82 172 8-8" className="support-hand" />
        <path d="M30 183v23m14-17v16" className="support-outline" />
      </g>
      <g className="support-clipboard">
        <rect
          x="211"
          y="98"
          width="72"
          height="90"
          rx="8"
          className="support-sheet-back"
        />
        <rect
          x="202"
          y="90"
          width="72"
          height="90"
          rx="8"
          className="support-sheet"
        />
        <rect
          x="223"
          y="85"
          width="30"
          height="13"
          rx="4"
          className="support-accent-fill"
        />
        <g className="support-line">
          <rect x="214" y="112" width="38" height="4" rx="2" />
          <rect x="214" y="122" width="48" height="4" rx="2" />
          <rect x="214" y="132" width="31" height="4" rx="2" />
        </g>
        <text x="214" y="160" className="support-tiny-text">
          ••• / •••.als
        </text>
      </g>
      <g className="support-check support-left-check">
        <circle cx="272" cy="176" r="14" className="support-success-fill" />
        <path d="m266 176 4 4 8-9" className="support-check-ink" />
      </g>
      <path className="support-cursor" d="m133 142 6 23 5-7 8 1z" />
      <g className="support-spark support-accent-stroke">
        <path d="M283 82v10m-5-5h10M188 101v8m-4-4h8" />
      </g>
    </svg>
  );
}

export function HandoffArrow() {
  return (
    <div className="support-handoff" aria-hidden="true">
      <svg viewBox="0 0 88 228" focusable="false">
        <path d="M8 112h66m-10-10 10 10-10 10" className="support-route-halo" />
        <path
          d="M8 112h66m-10-10 10 10-10 10"
          pathLength="1"
          className="support-route"
        />
        <g className="support-packet">
          <rect
            x="34"
            y="101"
            width="17"
            height="22"
            rx="3"
            className="support-sheet"
          />
          <path d="M38 108h9m-9 5h9m-9 5h5" className="support-accent-stroke" />
        </g>
      </svg>
    </div>
  );
}

export function DiscordScene() {
  return (
    <svg
      viewBox="0 0 320 228"
      className="support-scene"
      aria-hidden="true"
      focusable="false"
    >
      <ellipse cx="162" cy="207" rx="128" ry="8" className="support-shadow" />
      <rect
        x="29"
        y="24"
        width="265"
        height="143"
        rx="10"
        className="support-window"
      />
      <path d="M30 55h263" className="support-outline" />
      <path
        d="m44 36-2 11m9-11-2 11m-10-8h15m-16 5h15"
        className="support-discord-stroke"
      />
      <text x="65" y="44" className="support-tiny-text">
        studio-vault-support
      </text>
      <circle cx="277" cy="40" r="3" className="support-success-fill" />
      <g className="support-message">
        <circle cx="49" cy="80" r="10" className="support-accent-soft" />
        <path
          d="M45 80q4-7 8 0m-10 5q6-5 12 0"
          className="support-accent-stroke"
        />
        <text x="66" y="77" className="support-tiny-text">
          YOU
        </text>
        <rect
          x="65"
          y="86"
          width="151"
          height="38"
          rx="5"
          className="support-message-fill"
        />
        <path
          d="M77 97h123m-123 8h85m-85 8h101"
          className="support-report-lines"
        />
        <path d="m235 95 4 4 7-8m-7 8 7-8" className="support-discord-stroke" />
      </g>
      <g className="support-message">
        <circle cx="49" cy="143" r="10" className="support-discord-fill" />
        <path d="m44 143 3 3 6-7" className="support-check-ink" />
        <text x="66" y="146" className="support-small-text">
          Let’s take a look.
        </text>
        <g className="support-discord-fill">
          <circle className="support-typing-dot" cx="178" cy="144" r="1.6" />
          <circle className="support-typing-dot" cx="185" cy="144" r="1.6" />
          <circle className="support-typing-dot" cx="192" cy="144" r="1.6" />
        </g>
      </g>
      <g className="support-developer">
        <circle cx="44" cy="182" r="13" className="support-person-head" />
        <path d="M31 182v-7q11-14 24-1v9l-7-10-14 9" className="support-hair" />
        <path d="M32 183v-8q11-15 25 1v10" className="support-discord-stroke" />
        <path d="M26 213v-14q17-11 32 0l18 9" className="support-dev-sleeve" />
      </g>
      <rect
        x="91"
        y="151"
        width="195"
        height="65"
        rx="8"
        className="support-terminal"
      />
      <text x="104" y="167" className="support-micro-text">
        FIX → TEST → SHIP
      </text>
      <g className="support-code-line support-code">
        <path d="m105 180 4-3-4-3m8 6h37" />
      </g>
      <g className="support-code-line support-code">
        <path d="M122 188h81" />
      </g>
      <g className="support-code-line support-code">
        <path d="M122 195h55" />
      </g>
      <g className="support-bug">
        <circle cx="262" cy="181" r="7" className="support-warning-fill" />
        <path d="M262 177v5m0 3v1" className="support-check-ink" />
      </g>
      <g className="support-fixed">
        <rect
          x="102"
          y="173"
          width="132"
          height="29"
          rx="3"
          className="support-terminal-fill"
        />
        <text x="108" y="191" className="support-success-text">
          tests passing ✓
        </text>
        <rect
          className="support-terminal-caret support-success-fill"
          x="212"
          y="182"
          width="4"
          height="10"
          rx="1"
        />
      </g>
      <g className="support-check support-right-check">
        <circle cx="276" cy="202" r="14" className="support-success-fill" />
        <path d="m270 202 4 4 8-9" className="support-check-ink" />
      </g>
      <g className="support-wrench">
        <path
          d="m78 193-7-7 2-7 4 5 4-3-4-5 7 2 2 7-7 8"
          className="support-tool"
        />
      </g>
      <g className="support-spark support-accent-stroke">
        <path d="M300 184v12m-6-6h12M285 220v6m-3-3h6M231 211v8m-4-4h8" />
      </g>
    </svg>
  );
}
